import React from 'react';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import type { TeamMember } from '../types';


const TeamSection: React.FC = () => {
  const teamMembers: TeamMember[] = [
    {
      id: '1',
      name: 'Prince Rajput',
      role: 'Founder & AI Architect',
      bio: 'Visionary behind the automation framework. Prince specializes in building autonomous AI agents that handle complex business logic without human intervention.',
      image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&q=80", // Placeholder - replace with real photo
      alt: 'Portrait of Prince Rajput',
      linkedin: 'https://linkedin.com/in/prince262', // Add actual link
      certifications: ['AI Agent Specialist', 'Workflow Automation', 'Future Trends Analyst'],
      expertise: ['AI Architecture', 'Business Strategy', 'System Design']
    },
    {
      id: '2',
      name: 'Marcus Chen',
      role: 'Head of Engineering',
      bio: 'Marcus leads the technical execution. If an API exists, he can connect to it. He ensures every workflow is secure, scalable, and error-proof.',
      image: "https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?auto=format&fit=crop&q=80",
      alt: 'Portrait of Marcus Chen',
      linkedin: 'https://linkedin.com',
      certifications: ['AWS Certified Solution Architect', 'Make.com Expert Level 5'],
      expertise: ['Python', 'Node.js', 'Enterprise Security']
    },
    {
      id: '3',
      name: 'Sarah Williams',
      role: 'Lead Workflow Strategist',
      bio: 'Sarah translates business confusion into clear logic. She maps out the SOPs before a single line of code is written.',
      image: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?auto=format&fit=crop&q=80",
      alt: 'Portrait of Sarah Williams',
      linkedin: 'https://linkedin.com',
      certifications: ['n8n Advanced Partner', 'Six Sigma Green Belt'],
      expertise: ['Process Mapping', 'n8n', 'HubSpot Admin']
    }
    // Add more members as needed
  ];

  return (
    <section className="py-20 lg:py-28 bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-3xl sm:text-4xl font-bold text-text-primary mb-4">
            Meet the <span className="text-primary">Automation Architects</span>
          </h2>
          <p className="text-lg text-text-secondary">
            We are a team of engineers, strategists, and problem solvers obsessed with efficiency.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {teamMembers.map((member) => (
            <div
              key={member.id}
              className="group bg-card rounded-2xl border border-border overflow-hidden hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 flex flex-col"
            >
              {/* Image Header */}
              <div className="relative h-64 overflow-hidden bg-surface">
                 {/* Grayscale to Color hover effect */}
                <div className="absolute inset-0 bg-primary/20 group-hover:bg-transparent transition-colors duration-300 z-10 mix-blend-multiply" />
                <Image
                  src={member.image}
                  alt={member.name}
                  className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-500 transform group-hover:scale-105"
                />
                
                {/* Social Links Overlay */}
                <div className="absolute top-4 right-4 translate-x-10 opacity-0 group-hover:translate-x-0 group-hover:opacity-100 transition-all duration-300 z-20">
                  {member.linkedin && (
                    <a
                      href={member.linkedin}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center w-10 h-10 bg-white/90 backdrop-blur rounded-full text-[#0077b5] hover:bg-[#0077b5] hover:text-white transition-colors shadow-lg"
                    >
                      <Icon name="Linkedin" size={20} />
                    </a>
                  )}
                </div>
              </div>

              {/* Content Body */}
              <div className="p-6 flex-1 flex flex-col">
                <div className="mb-4">
                  <h3 className="text-xl font-bold text-text-primary mb-1">
                    {member.name}
                  </h3>
                  <p className="text-primary font-medium text-sm uppercase tracking-wide">
                    {member.role}
                  </p>
                </div>

                <p className="text-text-secondary text-sm leading-relaxed mb-6 flex-1">
                  {member.bio}
                </p>

                {/* Tags Section */}
                <div className="space-y-4 pt-4 border-t border-border">
                  
                  {/* Certifications */}
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                       <Icon name="Award" size={14} className="text-accent" />
                       <span className="text-xs font-semibold text-text-primary">Certifications</span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {member.certifications.map((cert, idx) => (
                        <span key={idx} className="px-2 py-1 bg-accent/5 border border-accent/20 rounded text-[10px] font-medium text-text-secondary">
                          {cert}
                        </span>
                      ))}
                    </div>
                  </div>

                   {/* Expertise */}
                   <div>
                    <div className="flex items-center gap-2 mb-2">
                       <Icon name="Cpu" size={14} className="text-primary" />
                       <span className="text-xs font-semibold text-text-primary">Expertise</span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {member.expertise.map((exp, idx) => (
                        <span key={idx} className="px-2 py-1 bg-primary/5 border border-primary/20 rounded text-[10px] font-medium text-text-secondary">
                          {exp}
                        </span>
                      ))}
                    </div>
                  </div>

                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default TeamSection;